
import { GoogleApiWrapper, InfoWindow, Map, Marker, Circle } from 'google-maps-react';
import React, { useEffect, useMemo, useState } from 'react';
import GooglePlacesAutocomplete, { geocodeByLatLng, geocodeByPlaceId, getLatLng } from 'react-google-places-autocomplete';
import Spinner from 'react-bootstrap/Spinner';
import { defaultLocation } from 'utils';
import useCurrentUser from 'hooks/useCurrentUser';
import { useNavigate } from 'react-router-dom';
import usePilotAvailableJobs from 'hooks/usePilotAvailableJobs';
import usePilotJobs from 'hooks/usePilotJobs';
import Images from 'assets/images';

const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY;


const GoogleMap = ({
    google,
    location,
    setLocation,
    setUserAddress,
    data,
    setData,
    mapStyling,
    loading,
    radius,
    pilots = [],
    mapTypeControl = true,
    streetViewControl = true,
    fullscreenControl = true
}) => {
    const [activeMarker, setActiveMarker] = useState(null);
    const [activeJob, setActiveJob] = useState(null);

    const navigate = useNavigate();
    const { user } = useCurrentUser();
    const { jobsData } = usePilotAvailableJobs({
        skip: 0,
        limit: 1000
    });
    const { jobsData: pilotJobsData } = usePilotJobs({
        status: 'InProgress',
        limit: 1000
    });
    const { totalAvailableJobs, availableJobs } = jobsData;
    const { totalJobs, jobs: pilotJobs } = pilotJobsData;

    const defaultMapStyling = {
        width: '100%',
        height: user?.roleType === 'PILOT' ? '95vh' : '80vh'
    }

    const handleDefaultAddress = (position) => {
        geocodeByLatLng(position)
            .then(res => {
                setUserAddress(res[0].formatted_address)
                setData({
                    label: res[0].formatted_address,
                    value: { place_id: '' }
                })
            })
            .catch(err => console.log(err))
    }

    useEffect(() => {
        if (location.lat === 0) {
            navigator.geolocation.getCurrentPosition(position => {
                const { latitude, longitude } = position.coords;
                const pos = {
                    lat: latitude,
                    lng: longitude
                }
                handleDefaultAddress(pos);
                setLocation(pos)
            }, (err) => {
                // setLocation(defaultLocation);
                // handleDefaultAddress(defaultLocation);
            });
        }
    }, [navigator.geolocation]);

    useEffect(() => {
        if (data?.value?.place_id) {
            geocodeByPlaceId(data?.value?.place_id)
                .then(results => {
                    getLatLng(results[0]).then(res => {
                        setLocation(res);
                        setUserAddress(data?.label);
                    })
                })
                .catch(error => console.error(error));
        }
    }, [data])

    const onMarkerDragEnd = (coord) => {
        const { latLng } = coord;
        const lat = latLng.lat();
        const lng = latLng.lng();
        const position = { lat, lng };
        geocodeByLatLng(position)
            .then(res => {
                setUserAddress(res[0].formatted_address);
                setData({
                    label: res[0].formatted_address,
                    value: { place_id: '' }
                })
            })
            .catch(err => console.log(err))
        setLocation(position);
    }

    const handleActiveMarker = (marker, job) => {
        setTimeout(() => {
            setActiveMarker(marker);
            setActiveJob(job);
        }, 50)
    };

    const memoizedMarkers = useMemo(() => {
        if (user?.roleType === 'PILOT') {
            return availableJobs?.map((job, index) => {
                const [lng, lat] = job?.coordinates;
                return (
                    <Marker
                        key={index}
                        position={{ lat, lng }}
                        icon={{
                            url: Images.droneMarker,
                            scaledSize: new google.maps.Size(60, 60)
                        }}
                        onClick={(props, marker) => handleActiveMarker(marker, job)}
                    />
                )
            });
        }
    }, [availableJobs]);

    const memoizedProgressMarkers = useMemo(() => {
        if (user?.roleType === 'PILOT') {
            return pilotJobs?.map((job, index) => {
                const [lng, lat] = job?.coordinates;
                return (
                    <Marker
                        key={index}
                        position={{ lat, lng }}
                        icon={{
                            url: Images.droneMarker,
                            scaledSize: new google.maps.Size(60, 60)
                        }}
                        onClick={(props, marker) => handleActiveMarker(marker, job)}
                    />
                )
            });
        }
    }, [pilotJobs]);

    const memoizedPilotMarkers = useMemo(() => {
        return pilots?.map((pilot, index) => {
            if (!pilot.coordinates || pilot.coordinates.length < 2) return null;
            const [lng, lat] = pilot.coordinates;
            return (
                <Marker
                    key={`pilot-${pilot._id || index}`}
                    position={{ lat, lng }}
                    icon={{
                        url: Images.droneMarker,
                        scaledSize: new google.maps.Size(60, 60)
                    }}
                    title={pilot.fullName}
                    onClick={(props, marker) => handleActiveMarker(marker, {
                        title: pilot.fullName,
                        address: pilot.address || pilot.bio,
                        avatar: pilot.avatar || "https://via.placeholder.com/60",
                        avgRating: pilot.avgRating || 0,
                        _id: pilot._id,
                        type: 'PILOT'
                    })}
                />
            )
        });
    }, [pilots]);

    const dynamicZoom = useMemo(() => {
        if (!radius) return 14;
        // Simple heuristic for zoom level based on radius in miles
        if (radius <= 1) return 14;
        if (radius <= 2) return 13;
        if (radius <= 5) return 12;
        if (radius <= 10) return 11;
        if (radius <= 25) return 10;
        if (radius <= 50) return 9;
        if (radius <= 100) return 8;
        if (radius <= 250) return 7;
        return 6;
    }, [radius]);

    useEffect(() => {
        if (activeJob?._id) {
            const el = document.getElementById("info-window-button");
            el && el.addEventListener('click', () => navigate(`/my-jobs/${activeJob?._id}`))
        }
    }, [activeJob])

    return (
        <>
            {loading ? <div className='d-flex justify-content-center'>
                <Spinner />
            </div> : (
                <>
                    <Map
                        google={google}
                        zoom={dynamicZoom}
                        style={mapStyling || defaultMapStyling}
                        initialCenter={location}
                        center={location}
                        mapTypeControl={mapTypeControl}
                        streetViewControl={streetViewControl}
                        fullscreenControl={fullscreenControl}
                    >
                        <Marker
                            name={'This is test name'}
                            position={location}
                            draggable={true}
                            onDragend={(t, map, coord) => onMarkerDragEnd(coord)}
                        />
                        {radius && (
                            <Circle
                                radius={radius * 1609.34} // radius in meters (1 mile = 1609.34 meters)
                                center={location}
                                strokeColor="#0000FF"
                                strokeOpacity={0.8}
                                strokeWeight={2}
                                fillColor="#0000FF"
                                fillOpacity={0.1}
                            />
                        )}
                        {memoizedMarkers}
                        {memoizedProgressMarkers}
                        {memoizedPilotMarkers}
                        <InfoWindow
                            marker={activeMarker}
                            visible={activeMarker}
                            onClose={() => {
                                setActiveJob(null);
                                setActiveMarker(null);
                            }}
                        >
                            <div>
                                <h5>{activeJob?.title}</h5>
                                <div>{activeJob?.address}</div>
                                {activeJob?.type !== 'PILOT' && (
                                    <button
                                        className='btn btn-sm bg-primary text-light mt-4 p-'
                                        id="info-window-button"
                                    >
                                        View Details
                                    </button>
                                )}
                            </div>
                        </InfoWindow>
                    </Map>
                    <GooglePlacesAutocomplete
                        apiKey={GOOGLE_MAP_API_KEY}
                        selectProps={{
                            value: data,
                            onChange: setData,
                            className: 'location-search pt-1',
                        }}
                    />
                </>
            )}
        </>
    )
}

export default GoogleApiWrapper({
    apiKey: GOOGLE_MAP_API_KEY
})(GoogleMap);
